import { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { LocationData } from "./types";
import { usd } from "./helpers";

const TOP_N_OPTIONS = [5, 10, 25, "All"] as const;
type TopN = (typeof TOP_N_OPTIONS)[number];

const colorFor = (index: number, total: number) => {
  const hue = Math.round((index * 360) / Math.max(total, 1));
  return `hsl(${hue}, 65%, 45%)`;
};

const visibleSetFor = (locations: LocationData[], n: TopN) => {
  const limit = n === "All" ? locations.length : n;
  return new Set(locations.slice(0, limit).map((l) => l.locationName));
};

interface HourlyChartProps {
  locations: LocationData[];
}

export const HourlyChart = ({ locations }: HourlyChartProps) => {
  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => b.gross - a.gross),
    [locations]
  );

  const [topN, setTopN] = useState<TopN>(10);
  const [visibleNames, setVisibleNames] = useState<Set<string>>(() =>
    visibleSetFor(sortedLocations, 10)
  );

  // Reset visibility when the underlying data changes (e.g., regenerate)
  useEffect(() => {
    setVisibleNames(visibleSetFor(sortedLocations, topN));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const handleTopNChange = (n: TopN) => {
    setTopN(n);
    setVisibleNames(visibleSetFor(sortedLocations, n));
  };

  const togglePill = (name: string) => {
    setVisibleNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const { data, lines } = useMemo(() => {
    const hourSet = new Set<string>();
    sortedLocations.forEach((l) => l.hourly.forEach((h) => hourSet.add(h.hour)));
    const sortedHours = Array.from(hourSet).sort();

    const rows = sortedHours.map((hour) => {
      const row: Record<string, string | number> = {
        hour,
        label: moment(hour).format("ha").toUpperCase(),
      };
      sortedLocations.forEach((loc) => {
        const bucket = loc.hourly.find((h) => h.hour === hour);
        row[loc.locationName] = bucket ? bucket.gross / 100 : 0;
      });
      return row;
    });

    const lineConfigs = sortedLocations.map((loc, i) => ({
      name: loc.locationName,
      color: colorFor(i, sortedLocations.length),
      gross: loc.gross,
    }));

    return { data: rows, lines: lineConfigs };
  }, [sortedLocations]);

  if (data.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <h2 className="text-lg font-bold text-gray-900">Hourly Sales by Location</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Showing {visibleNames.size} of {sortedLocations.length}
          </span>
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            {TOP_N_OPTIONS.map((opt) => (
              <button
                key={String(opt)}
                onClick={() => handleTopNChange(opt)}
                className={`px-3 py-1.5 transition-colors ${
                  topN === opt
                    ? "bg-red-700 text-white font-semibold"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {opt === "All" ? "All" : `Top ${opt}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" stroke="#6b7280" fontSize={12} />
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={(v: number) => `$${v.toLocaleString()}`}
          />
          <Tooltip
            formatter={(v: number) =>
              `$${v.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            }
            labelFormatter={(label, payload) => {
              const hour = payload?.[0]?.payload?.hour;
              return hour ? moment(hour as string).format("ddd, h A") : String(label);
            }}
            contentStyle={{ borderRadius: 6, fontSize: 13, borderColor: "#e5e7eb" }}
            itemSorter={(item) => -(item.value as number)}
          />
          {lines.map((l) => (
            <Line
              key={l.name}
              type="monotone"
              dataKey={l.name}
              stroke={l.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              hide={!visibleNames.has(l.name)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Custom legend */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500 mb-2">Click to toggle a location</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 max-h-64 overflow-y-auto">
          {lines.map((l) => {
            const visible = visibleNames.has(l.name);
            return (
              <button
                key={l.name}
                onClick={() => togglePill(l.name)}
                className={`flex items-center gap-2 px-2 py-1 rounded text-xs text-left transition-opacity hover:bg-gray-50 ${
                  visible ? "" : "opacity-40"
                }`}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: l.color }}
                />
                <span className="truncate flex-1 text-gray-700">{l.name}</span>
                <span className="text-gray-400 tabular-nums whitespace-nowrap">
                  {usd(l.gross)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
